import _memoize from 'lodash/memoize';
import { getFont } from '@tornado-rdk/typography';
import { PRIMARY, makeThemeColor, DEFAULT_MODE } from '@tornado-rdk/theme';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import { colors as progressColors, HEIGHT as PROGRESS_HEIGHT } from '../progress/progress.style';
import { identity } from '../utils/sizes';
import { cacheResolver, FOCUSED_Z_INDEX, makeDefaultStylingContext, UNFOCUSED_Z_INDEX } from '../utils/style';
import {
  BORDER_RADIUS,
  BORDER_WIDTH,
  SCALE_FOCUSED,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import {
  BORDER_COLOR,
  DESCRIPTION_FONT_NAME,
  DESCRIPTION_FONT_TYPE,
  DESCRIPTION_LINES,
  ICON_HEIGHT,
  ICON_PADDING_HORIZONTAL,
  ICON_PADDING_VERTICAL,
  ICONS,
  INNER_BORDER_COLOR,
  TITLE_FONT_NAME,
  TITLE_FONT_TYPE,
  TITLE_LINES,
} from './squareMeta.constants';
import { TRANSPARENT } from '../utils/colors';

export { PROGRESS_HEIGHT };

export { TITLES_ORDER, IMAGE_MARGIN_BOTTOM, ICON_HEIGHT, ICON_PADDING_VERTICAL } from './squareMeta.constants';

export const defaultContext = makeDefaultStylingContext();

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_HEIGHT[screenSize],
}));

export const colors = {
  title: makeThemeColor(PRIMARY),
  description: makeThemeColor(PRIMARY, 0.6),
  border: BORDER_COLOR,
  innerBorder: INNER_BORDER_COLOR,
};

export const IMAGE_OFFSET = {
  [HD]: BORDER_WIDTH[HD] * 2,
  [FULL_HD]: BORDER_WIDTH[FULL_HD] * 2,
};

export const getIcon = (icons, position) => (icons ? icons[ICONS[position]] : null);

export const makeBasicIconStyle = () => ({
  alpha: 0.8,
  y: ICON_PADDING_VERTICAL,
});

export const getDetailsPosition = _memoize((hasHighlightText = true) =>
  hasHighlightText
    ? {
        mountX: 1,
        x: identity,
      }
    : {
        mountX: 0,
        x: 0,
      },
);

export const getInitialStyle = _memoize(
  ({ screenSize = FULL_HD, colorMode = DEFAULT_MODE } = defaultContext) => ({
    zIndex: UNFOCUSED_Z_INDEX,
    Border: {
      alpha: 0,
      zIndex: 1,
      x: -BORDER_WIDTH[screenSize],
      y: -BORDER_WIDTH[screenSize],
      shader: {
        outline: BORDER_WIDTH[screenSize],
        radius: BORDER_RADIUS[screenSize],
        strokeColor: TRANSPARENT,
      },
    },
    Image: {
      zIndex: 2,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        progressHeight: PROGRESS_HEIGHT,
        progressBackgroundColor: progressColors.background[colorMode],
        progressBarColor: progressColors.bar[colorMode],
      },
    },
    TopLeftIcon: {
      zIndex: 3,
      x: ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    TopRightIcon: {
      zIndex: 3,
      ...makeBasicIconStyle(),
      mountX: 1,
      x: width => width - ICON_PADDING_HORIZONTAL,
      y: ICON_PADDING_VERTICAL,
    },
    BottomRightIcon: {
      zIndex: 3,
      mount: 1,
      x: w => w - ICON_PADDING_HORIZONTAL,
      alpha: 0.8,
    },
    Incentive: {
      zIndex: 0,
    },
    Title: {
      color: colors.title[colorMode],
      text: {
        ...getFont(TITLE_FONT_NAME, screenSize, TITLE_FONT_TYPE),
        maxLines: TITLE_LINES,
        verticalAlign: 'bottom',
        wordWrap: true,
        wordBreak: true,
        advancedRenderer: true,
        maxLinesSuffix: '...',
      },
    },
    Description: {
      color: colors.description[colorMode],
      text: {
        ...getFont(DESCRIPTION_FONT_NAME, screenSize, DESCRIPTION_FONT_TYPE),
        maxLines: DESCRIPTION_LINES,
        verticalAlign: 'middle',
        wordWrap: true,
        maxLinesSuffix: '...',
      },
    },
    ProgressBar: {
      mountY: 1,
    },
  }),
  cacheResolver,
);

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    zIndex: FOCUSED_Z_INDEX,
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': colors.border[colorMode],
        'shader.innerColor': colors.innerBorder[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Image: {
      smooth: {
        scale: SCALE_FOCUSED,
        'shader.scale': SCALE_FOCUSED,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  zIndex: UNFOCUSED_Z_INDEX,
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
